import type { ReportView } from '@/data/schemas';
import type {
  NavSection,
  ReportCurrentRoute,
  ReportNavNode,
  ReportNavigation,
  ReportTopLevelSection,
} from '../types';

export function buildNavigation(view: ReportView): ReportNavigation {
  const nodes: ReportNavNode[] = view.toc.map((group) => {
    const sections: NavSection[] = group.items
      .map((item) => {
        if (item.type === 'section') {
          return {
            id: item.sectionId,
            label: item.label,
            anchor: item.sectionId,
            sectionIds: [item.sectionId],
          };
        }

        if (item.type === 'collection') {
          const sectionIds = item.entries.map((entry) => entry.sectionId);
          const anchor = sectionIds[0];
          if (!anchor) return null;
          return {
            id: anchor,
            label: item.label,
            anchor,
            sectionIds,
          };
        }

        return null;
      })
      .filter((section): section is NavSection => Boolean(section));

    return {
      id: group.id as ReportTopLevelSection,
      label: group.label,
      icon: group.icon,
      order: group.order,
      sections,
    };
  });

  return {
    nodes: nodes.sort((a, b) => a.order - b.order),
  };
}

export function deriveRouteFromLocation(
  nav: ReportNavigation,
  _pathname: string,
  search: Record<string, unknown>,
  hash: string
): ReportCurrentRoute {
  const nodes = nav.nodes;
  const normalizedHash = hash
    ? hash.replace(/^#/, '')
    : typeof search?.subsection === 'string'
      ? search.subsection.replace(/^#/, '')
      : undefined;

  const requestedTopLevel = typeof search?.section === 'string' ? search.section : undefined;

  let selectedNode = requestedTopLevel
    ? nodes.find((node) => node.id === requestedTopLevel)
    : undefined;

  if (!selectedNode) {
    selectedNode = nodes[0];
  }

  return {
    topLevel: selectedNode?.id ?? '',
    hash: normalizedHash,
  };
}

export function isSameRoute(a: ReportCurrentRoute, b: ReportCurrentRoute): boolean {
  return a.topLevel === b.topLevel && a.hash === b.hash;
}

export function formatBreadcrumbLabel(hash: string): string {
  return hash.replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}
